import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import type { MultiFileUploadV2FileItem as FileItem } from 'shared-components';
import { IApplicantOfferRequirementTask } from 'types';
import { ApplicationState } from '@/store';
import { useDocumentSave } from './useDocumentSave';

interface UseInternalDocumentUploadParams {
  requirement: IApplicantOfferRequirementTask;
  offerId: string;
}

export interface UseInternalDocumentUploadResult {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  docKey: string;
  onProceed: (files: FileItem[], deletedS3Keys?: string[]) => void;
  uploadedSuccessfully: boolean;
  fileUploadError: string | null;
  existingFiles: { s3Keys: string[]; urls?: string[] } | undefined;
}

export function useInternalDocumentUpload({
  requirement,
  offerId,
}: UseInternalDocumentUploadParams): UseInternalDocumentUploadResult {
  const { handleSaveInternalDocument } = useDocumentSave();
  const [isOpen, setIsOpen] = useState(false);

  const { internalUploadedSuccessfully, internalFileUploadError } = useSelector(
    (state: ApplicationState) => state.student.documents,
  );

  const docKey = useMemo(
    () => `staffDocument-${requirement.id.replace(/#/g, '-')}-${offerId}`,
    [requirement.id, offerId],
  );

  const onOpen = useCallback((): void => setIsOpen(true), []);
  const onClose = useCallback((): void => setIsOpen(false), []);

  const onProceed = useCallback(
    (files: FileItem[], deletedS3Keys?: string[]): void => {
      const newFileNames: string[] = [];
      const newS3Keys: string[] = [];

      for (const file of files) {
        const fileWithMeta = file as FileItem & { url?: string; s3Key?: string };
        const derivedKey =
          fileWithMeta.s3Key || (fileWithMeta.url ? fileWithMeta.url.split('/').slice(3).join('/').split('?')[0] : '');
        if (derivedKey) {
          newS3Keys.push(derivedKey);
          newFileNames.push(file.name);
        }
      }

      if (newS3Keys.length === 0 && (deletedS3Keys?.length ?? 0) === 0) {
        toast.error('No valid files could be processed. Please try uploading again.');
        return;
      }

      const toDelete = new Set(deletedS3Keys ?? []);
      const internalS3Keys = requirement.internalData?.s3Keys ?? [];
      const internalFileNames = requirement.internalData?.fileNames ?? [];
      const s3KeyToFileName = new Map<string, string>(
        internalS3Keys.map((key, i) => [key, internalFileNames[i] ?? '']),
      );
      const existingS3Keys = internalS3Keys.filter(k => !toDelete.has(k));
      const existingFileNames = existingS3Keys.map(k => s3KeyToFileName.get(k) ?? '');

      handleSaveInternalDocument({
        requirementId: requirement.id,
        docKey,
        s3Keys: [...existingS3Keys, ...newS3Keys],
        fileNames: [...existingFileNames, ...newFileNames],
        newS3Keys,
        removedS3Keys: deletedS3Keys ?? [],
      });
    },
    [handleSaveInternalDocument, requirement.id, requirement.internalData, docKey],
  );

  const existingFiles = requirement.internalData
    ? { s3Keys: requirement.internalData.s3Keys, urls: requirement.internalData.urls }
    : undefined;

  return {
    isOpen,
    onOpen,
    onClose,
    docKey,
    onProceed,
    uploadedSuccessfully: internalUploadedSuccessfully,
    fileUploadError: internalFileUploadError,
    existingFiles,
  };
}
